import PropTypes from "prop-types";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useFormik, Form, FormikProvider } from "formik";
// material
import { Card, Alert, TextField, Stack, CardHeader, Box } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useAuth from "../../../hooks/useAuth";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
import { CheckPerm } from "../../../utils/apis";

// ----------------------------------------------------------------------
BranchUpdate.propTypes = {
  myProfile: PropTypes.object,
  uid: PropTypes.string,
  fetch: PropTypes.func,
};
export default function BranchUpdate({ myProfile, uid, fetch }) {
  const { updateBranch } = useAuth();
  const hasPermission = CheckPerm("update_branches");
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  const Schema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    location: Yup.string(),
    phone: Yup.string(),
    email: Yup.string(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: myProfile.name || "",
      location: myProfile.location || "",
      phone: myProfile.phone || "",
      email: myProfile.email || "",
    },
    validationSchema: Schema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        await updateBranch(values, uid);
        enqueueSnackbar("Branch's data has been updated!", {
          variant: "success",
        });
        if (isMountedRef.current) {
          setSubmitting(false);
        }
        fetch();
      } catch (error) {
        if (isMountedRef.current) {
          setErrors({ afterSubmit: error.message });
          setSubmitting(false);
        }
      }
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <Card
      variant="outlined"
      sx={{
        border: "0.4px solid",
        borderColor: "background.border",
        boxShadow:
          "4px 2px 124px rgba(46, 53, 58, 0.02), 0px 4px 124px rgba(46, 41, 78, 0.02)",
        borderRadius: "8px",
        px: 3,
        pb: 3,
      }}
    >
      <CardHeader title="Branch Information" />
      <Box sx={{ p: { md: 3, xs: 1 } }}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {errors.afterSubmit && (
                <Alert severity="error">{errors.afterSubmit}</Alert>
              )}
              <Stack direction="column" spacing={2}>
                <TextField
                  disabled={!hasPermission}
                  fullWidth
                  label="Name"
                  {...getFieldProps("name")}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />
                <TextField
                  disabled={!hasPermission || !myProfile.canUpdateLocation}
                  fullWidth
                  label="Location"
                  {...getFieldProps("location")}
                  error={Boolean(touched.location && errors.location)}
                  helperText={
                    myProfile.canUpdateLocation
                      ? touched.location && errors.location
                      : "This branch has been selected for ecommerce so you cannot updates its location here. Please go to Store Settings to update it."
                  }
                />
                <TextField
                  disabled={!hasPermission}
                  fullWidth
                  label="Phone Number(s)"
                  {...getFieldProps("phone")}
                  error={Boolean(touched.phone && errors.phone)}
                  helperText={
                    (touched.phone && errors.phone) ||
                    "Add multiple phone numbers by separating them with comma."
                  }
                />
                <TextField
                  disabled={!hasPermission}
                  fullWidth
                  label="Email Address(es)"
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  helperText={
                    (touched.email && errors.email) ||
                    "Add multiple email addresses by separating them with comma."
                  }
                />
              </Stack>
            </Stack>
            {hasPermission && (
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ mt: 3 }}
              >
                Update
              </LoadingButton>
            )}
          </Form>
        </FormikProvider>
      </Box>
    </Card>
  );
}
